import {
  Browser,
  Geo,
  HttpClient,
  Logger,
  LoggerTransformer,
  OperatingSystem,
  Skin,
  WikiConfig,
} from "@identity/utils";
import {
  DataWarehouseParams,
  TimeBasedParams,
  TrackingParams,
} from "@identity/services";
import { OptInService } from "../OptInService";
import { inject, injectable, injectWithTransform } from "tsyringe";
import { WINDOW } from "@identity/symbols";
import { ExperimentBucketing } from "../../utils/experiment-bucketing";

@injectable()
export class DataWarehouseSender {
  private readonly startTime: number;
  private readonly tzOffset: number;

  constructor(
    @injectWithTransform(Logger, LoggerTransformer, "DataWarehouseSender")
    private readonly logger: Logger,
    @inject(HttpClient) private readonly httpClient: HttpClient,
    @inject(Skin) private readonly skin: Skin,
    @inject(OptInService) private readonly optInService: OptInService,
    @inject(WINDOW) private readonly window: Window,
    @inject(WikiConfig) private readonly wikiConfig: WikiConfig,
    @inject(Geo) private readonly geo: Geo,
    @inject(Browser) private readonly browser: Browser,
    @inject(OperatingSystem) private readonly operatingSystem: OperatingSystem,
    @inject(ExperimentBucketing)
    private readonly experimentBucketing: ExperimentBucketing,
  ) {
    const now = new Date();
    this.startTime = this.getTimeOrigin();
    this.tzOffset = now.getTimezoneOffset();
  }

  private getTimeOrigin() {
    return window.performance?.timeOrigin;
  }

  getCurrentTimestamp(): number {
    return Date.now();
  }

  getStartTime(): number {
    return this.startTime;
  }

  getTimezoneOffset(): number {
    return this.tzOffset;
  }

  public getTrackingParams(): TrackingParams {
    const optedOutValue = "opted-out";

    return {
      browser: `${this.operatingSystem.getOperatingSystem()} ${this.browser.getBrowser()}`,
      browser_ts: this.getCurrentTimestamp(),
      tz_offset: this.getTimezoneOffset(),
      country: this.geo.getCountryCode() || "",
      session_id: this.optInService.trackingAllowed
        ? this.window.fandomContext?.tracking?.sessionId
        : optedOutValue,
      pv_number: this.window.fandomContext?.tracking?.pvNumber,
      pv_number_global: this.window.fandomContext?.tracking?.pvNumberGlobal,
      pv_unique_id: this.window.fandomContext?.tracking?.pvUID,
      beacon: this.optInService.trackingAllowed
        ? this.window.fandomContext?.tracking?.beaconId
        : optedOutValue,
      ua: this.window.navigator?.userAgent,
      c: this.wikiConfig.get("wgCityId") || "unknown",
      ck: this.wikiConfig.get("dsSiteKey") || "unknown",
      lc: this.wikiConfig.get("wgUserLanguage") || "unknown",
      s: this.skin.get(),
      u: this.optInService.trackingAllowed
        ? this.wikiConfig.get("wgUserId") || 0
        : -1,
      a: this.window.fandomContext?.page?.articleId || -1,
      x: this.wikiConfig.get("wgDBname") || "unknown",
      n: this.wikiConfig.get("wgNamespaceNumber") || -1,
      exp_bucket: this.optInService.trackingAllowed
        ? this.experimentBucketing.getCurrentBucket()
        : optedOutValue,
    };
  }

  public getTimeBasedParams(): TimeBasedParams {
    return {
      cb: Math.floor(Math.random() * 99999),
      url: document.URL,
    };
  }

  public buildDataWarehouseUrl(
    params: DataWarehouseParams,
    baseUrl: string,
  ): string {
    if (!baseUrl) {
      this.logger.error("Error building DWH tracking URL");
      throw new Error(
        "Error while building DWH tracking URL - no baseUrl provided",
      );
    }

    const queryParams = new URLSearchParams(
      params as unknown as Dictionary<string>,
    );
    queryParams.sort();
    return `${baseUrl}?${queryParams.toString()}`;
  }

  public sendEvent(params: DataWarehouseParams, trackingURL: string): void {
    const url = this.buildDataWarehouseUrl(params, trackingURL);

    if (url) {
      this.httpClient.get(url, { mode: "cors" }, 5000)?.catch((err) => {
        this.logger.error("Failed to send data", err);
      });
    }
  }
}
